import React from "react";
import { Button, Form, Input as AntInput, notification, Typography, Checkbox, Alert } from "antd";
import { useMutation } from "react-query";
import { loginNew } from "../../../service/authService";
import styled from "styled-components";
import { AuthLayout } from "../auth-layout/AuthLayout";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const { Title, Text, Link } = Typography;

interface LoginFormProps {
  refetch: any;
}

const StyledTitle = styled(Title)`
  &.ant-typography {
    margin-bottom: 12px !important;
    color: #FEFFFC !important;
    font-weight: 500;
    text-align: center;
    font-size: clamp(2.5rem, 5vw, 4rem) !important;
  }
`;

const StyledSubtitle = styled(Text)`
  display: block;
  margin-bottom: 48px;
  color: #FEFFFC;
  font-size: 18px;
  text-align: center;
  opacity: 0.9;
`;

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 24px;

  .ant-form-item-label > label {
    color: #FEFFFC !important;
    font-size: 14px;
    font-weight: 500;
    height: auto;
  }

  /* Target both regular inputs and password wrappers */
  .ant-input-affix-wrapper,
  .ant-input:not(.ant-input-affix-wrapper > input) {
    height: 52px;
    background-color: #263e44 !important;
    border: 1px solid rgba(255, 255, 255, 0.4) !important;
    border-radius: 8px !important;
    color: #FEFFFC !important;
    padding: 0 12px !important;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;

    &:hover, &:focus, &-focused {
      border-color: #EBFED1 !important;
      background-color: #2c474d !important;
      box-shadow: none !important;
    }

    /* Inner input for affix-wrapper (password/prefix) */
    input {
      background-color: transparent !important;
      color: #FEFFFC !important;
      border: none !important;
      height: 100% !important;
      padding: 0 !important;

      &::placeholder {
        color: rgba(255, 255, 255, 0.5) !important;
      }
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.5) !important;
    }
  }

  .ant-input-password-icon {
    color: rgba(255, 255, 255, 0.5) !important;
    &:hover {
      color: #FEFFFC !important;
    }
  }

  .ant-form-item-explain-error {
    font-size: 12px;
    margin-top: 4px;
    color: #ff4d4f;
  }
`;

const StyledButton = styled(Button)``;

const RememberForgotRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  
  .ant-checkbox-wrapper {
    color: #FEFFFC;
    opacity: 0.9;
    font-size: 14px;
  }

  .ant-checkbox-inner {
    background-color: #263e44;
    border-color: rgba(255, 255, 255, 0.4);
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #EBFED1;
    border-color: #EBFED1;
  }
  
  .ant-checkbox-checked .ant-checkbox-inner::after {
    border-color: #1E363C;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const StyledLink = styled(Link)`
  color: #FEFFFC !important;
  font-weight: 600;
  text-decoration: underline !important;
  
  &:hover {
    color: #EBFED1 !important;
  }
`;

const RegisterPrompt = styled.div`
  text-align: center;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
`;

export const LoginFormNew: React.FC<LoginFormProps> = ({
  refetch,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [verificationError, setVerificationError] = React.useState<string | null>(null);

  const { mutate, isLoading } = useMutation(loginNew, {
    onSuccess: async (data) => {
      setVerificationError(null);
      notification.success({
        message: t("auth.loginSuccessTitle"),
        description: t("auth.loginSuccessDesc", { name: data.user?.firstName || "" }),
        placement: "bottomRight",
        duration: 2,
      });

      setTimeout(() => {
        window.location.href = "/";
      }, 1000);
    },
    onError: (error: any) => {
      if (error?.response?.data?.requiresEmailVerification) {
        setVerificationError(error.response.data.error);
        return;
      }
      setVerificationError(null);

      const errorMessage = error?.response?.data?.error || t("auth.serverError");

      notification.error({
        message: t("auth.loginFailTitle"),
        description: errorMessage,
        placement: "bottomRight",
      });
    },
  });

  const handleSubmit = (values: any) => {
    mutate({
      email: values.email,
      password: values.password,
    });
  };

  return (
    <AuthLayout maxWidth={540}>
      <StyledTitle level={1}>
        {t("auth.welcomeBack")}
      </StyledTitle>
      <StyledSubtitle>{t("auth.loginSubtitle")}</StyledSubtitle>

      <Form form={form} onFinish={handleSubmit} layout="vertical" requiredMark={false}>
        <StyledFormItem
          name="email"
          label={t("auth.email")}
          rules={[
            { required: true, message: t("auth.emailRequired") },
            {
              pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: t("auth.emailInvalid")
            },
          ]}
          normalize={(value) => value?.trim().toLowerCase()}
        >
          <AntInput
            placeholder={t("auth.emailPlaceholder")}
            size="large"
            autoComplete="email"
          />
        </StyledFormItem>

        <StyledFormItem
          name="password"
          label={t("auth.password")}
          rules={[{ required: true, message: t("auth.passwordRequired") }]}
        >
          <AntInput.Password
            placeholder={t("auth.passwordPlaceholder")}
            size="large"
            autoComplete="current-password"
          />
        </StyledFormItem>

        <RememberForgotRow>
          <Form.Item name="remember" valuePropName="checked" noStyle>
            <Checkbox>{t("auth.rememberMe")}</Checkbox>
          </Form.Item>
          <StyledLink onClick={() => navigate("/reset-password")}>{t("auth.forgotPassword")}</StyledLink>
        </RememberForgotRow>

        {verificationError && (
          <Alert
            type="error"
            message={verificationError}
            description={
              <div style={{ marginTop: 8 }}>
                <a
                  onClick={() => navigate("/resend-verification")}
                  style={{ color: "#1677ff", fontWeight: 500, cursor: "pointer", textDecoration: "underline" }}
                >
                  {t("auth.resendVerification")}
                </a>
              </div>
            }
            showIcon
            style={{ marginBottom: 24, borderRadius: 8 }}
          />
        )}

        <Form.Item style={{ marginBottom: 0 }}>
          <StyledButton type="primary" htmlType="submit" loading={isLoading} block>
            {isLoading ? t("auth.loggingIn") : t("auth.login")}
          </StyledButton>
        </Form.Item>
      </Form>

      <RegisterPrompt>
        <Text style={{ color: '#FEFFFC', opacity: 0.8 }}>
          {t("auth.noAccount")}{" "}
          <StyledLink onClick={() => navigate("/register")}>
            {t("auth.registerFree")}
          </StyledLink>
        </Text>
      </RegisterPrompt>
    </AuthLayout>
  );
};
